import { Crown, Mic, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { AudioTrackOption } from '@/lib/upload/buildAudioSelection';
import { SUBTITLE_LANGUAGES, languageLabel, primaryLanguage } from '@/lib/languages/subtitleLanguages';

// text-left: a truncated label would otherwise inherit the button's centered text.
const TRIGGER_CLASS =
  'h-10 sm:h-12 text-sm sm:text-base text-left shadow-sm focus:shadow-glow border-primary/20 focus:border-primary/40 transition-all duration-300';
const LABEL_CLASS = 'text-sm sm:text-base font-semibold mb-2 flex items-center gap-2 text-foreground';

type Props = {
  tracks: readonly AudioTrackOption[];
  showTrackSelect: boolean;
  showManualAudio: boolean;
  selectedTrackCode: string | null;
  onTrackChange: (code: string) => void;
  manualAudioLanguage: string;
  onManualAudioChange: (code: string) => void;
  /** Effective audio language, or null while no track is decided. */
  audioLanguage: string | null;
  subtitleLanguage: string;
  onSubtitleChange: (code: string) => void;
  hasPro: boolean;
};

/** Audio track, audio language and subtitle language pickers of a YouTube import.
 *  Stateless: UploadModal owns the choices. */
export const YoutubeAudioFields = ({
  tracks,
  showTrackSelect,
  showManualAudio,
  selectedTrackCode,
  onTrackChange,
  manualAudioLanguage,
  onManualAudioChange,
  audioLanguage,
  subtitleLanguage,
  onSubtitleChange,
  hasPro,
}: Props) => {
  const { t, i18n } = useTranslation();
  const ui = i18n.language;

  const trackLabel = (track: AudioTrackOption) => {
    const base = languageLabel(track.language ?? track.code ?? '?', ui);
    const suffix =
      track.kind === 'original'
        ? ` (${t('uploadModal.trackOriginalSuffix')})`
        : track.kind === 'auto_dub'
          ? ` (${t('uploadModal.trackAutoDubSuffix')})`
          : '';
    return `${base}${suffix}`;
  };

  return (
    <>
      {showTrackSelect && (
        <div className="space-y-3">
          <label className={LABEL_CLASS}>
            <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            {t('uploadModal.audioTrack')}
          </label>
          <Select value={selectedTrackCode ?? ''} onValueChange={onTrackChange}>
            <SelectTrigger className={TRIGGER_CLASS} aria-label={t('uploadModal.audioTrack')}>
              <SelectValue placeholder={t('uploadModal.chooseAudioTrack')} />
            </SelectTrigger>
            <SelectContent>
              {tracks.filter((track) => track.code).map((track) => (
                <SelectItem key={track.code as string} value={track.code as string} disabled={!track.transcribable}>
                  {trackLabel(track)}
                  {!track.transcribable && ` - ${t('uploadModal.trackUnsupported')}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showManualAudio && (
        <div className="space-y-3">
          <label className={LABEL_CLASS}>
            <Mic className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            {t('uploadModal.audioLanguage')}
          </label>
          <Select value={manualAudioLanguage} onValueChange={onManualAudioChange}>
            <SelectTrigger className={TRIGGER_CLASS} aria-label={t('uploadModal.audioLanguage')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUBTITLE_LANGUAGES.map((code) => (
                <SelectItem key={code} value={code}>{languageLabel(code, ui)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-3">
        <label className={LABEL_CLASS}>
          <Languages className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          {t('uploadModal.subtitleLanguage')}
        </label>
        <Select value={subtitleLanguage} onValueChange={onSubtitleChange}>
          <SelectTrigger className={TRIGGER_CLASS} aria-label={t('uploadModal.subtitleLanguage')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUBTITLE_LANGUAGES.map((code) => {
              const locked = !hasPro && primaryLanguage(code) !== primaryLanguage(audioLanguage);
              return (
                <SelectItem key={code} value={code} disabled={locked}>
                  <div className="flex items-center gap-2">
                    <span>{languageLabel(code, ui)}</span>
                    {locked && (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                        <Crown className="w-3 h-3 mr-1" />
                        PRO
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {!hasPro && <p className="text-xs text-muted-foreground">{t('uploadModal.subtitleProHint')}</p>}
      </div>
    </>
  );
};
